import { useMemo, useState } from "react";
import { CheckCheck } from "lucide-react";
import HelpQ from "./HelpQ";

const INITIAL_VISIBLE_COUNT = 10;
const SHOW_MORE_STEP = 10;

function AppRightRailTaskAgentSection({
  taskAgentTab,
  activeTaskGroups,
  taskLogGroups,
  onSetTaskAgentTab,
  onOpenTaskModal,
  styles,
}) {
  const [visibleByGroup, setVisibleByGroup] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  const getVisibleLimit = (key) =>
    visibleByGroup[key] || INITIAL_VISIBLE_COUNT;

  const showMore = (key) => {
    setVisibleByGroup((prev) => ({
      ...prev,
      [key]: (prev[key] || INITIAL_VISIBLE_COUNT) + SHOW_MORE_STEP,
    }));
  };

  const taskMatchesSearch = (task, query) => {
    const q = String(query || "").trim().toLowerCase();
    if (!q) return true;
    return [
      task?.title,
      task?.statusLabel,
      task?.outcomeLabel,
      task?.focusArea,
      task?.dateLabel,
      task?.searchText,
    ]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(q));
  };

  const filterGroups = (groups, query, labelKey) =>
    (groups || [])
      .map((group) => {
        const label = group?.[labelKey] || "";
        const labelMatches = String(label).toLowerCase().includes(String(query || "").trim().toLowerCase());
        const tasks = labelMatches
          ? group.tasks || []
          : (group.tasks || []).filter((task) => taskMatchesSearch(task, query));
        return { ...group, tasks };
      })
      .filter((group) => group.tasks.length > 0);

  const activeGroups = useMemo(
    () => filterGroups(activeTaskGroups, searchQuery, "focusArea"),
    [activeTaskGroups, searchQuery],
  );
  const logGroups = useMemo(
    () => filterGroups(taskLogGroups, searchQuery, "dateLabel"),
    [taskLogGroups, searchQuery],
  );

  return (
    <>
      <section className={styles.taskAgentSection} data-tour-id="task-agent">
        <div className={styles.taskAgentHeader}>
          <div className={styles.taskAgentLabelWrap}>
            <span className={styles.taskAgentIcon}>
              <CheckCheck size={10} strokeWidth={2.5} aria-hidden="true" />
            </span>
            <span className={styles.taskAgentLabel}>Task agent</span>
            <HelpQ stepIndex={10} title="Track your tasks" />
          </div>
          <div className={styles.taskAgentTabs}>
            <button
              type="button"
              className={`${styles.taskAgentTab} ${
                taskAgentTab === "active" ? styles.taskAgentTabActive : ""
              }`}
              onClick={() => onSetTaskAgentTab("active")}
            >
              Active
            </button>
            <button
              type="button"
              className={`${styles.taskAgentTab} ${
                taskAgentTab === "log" ? styles.taskAgentTabActive : ""
              }`}
              onClick={() => onSetTaskAgentTab("log")}
            >
              Log
            </button>
          </div>
        </div>
        <div className={styles.taskAgentSearchRow}>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            className={styles.taskAgentSearchInput}
            placeholder="Search products, blogs, actions..."
            aria-label="Search task agent actions"
          />
          {searchQuery ? (
            <button
              type="button"
              className={styles.taskAgentSearchClear}
              onClick={() => setSearchQuery("")}
              aria-label="Clear task search"
            >
              Clear
            </button>
          ) : null}
        </div>
      </section>

      {taskAgentTab === "active" ? (
        <div className={styles.taskAgentList}>
          {activeGroups.length ? (
            activeGroups.map((group) => {
              const key = `active:${group.focusArea}`;
              const visibleLimit = getVisibleLimit(key);
              const visibleTasks = group.tasks.slice(0, visibleLimit);
              const remaining = Math.max(0, group.tasks.length - visibleLimit);
              return (
                <div key={group.focusArea} className={styles.taskAgentGroup}>
                  <div className={styles.taskAgentGroupLabel}>
                    {group.focusArea}
                    {group.tasks.length > INITIAL_VISIBLE_COUNT
                      ? ` · ${group.tasks.length}`
                      : ""}
                  </div>
                  {visibleTasks.map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      className={styles.taskAgentItem}
                      onClick={() => onOpenTaskModal(task, "active")}
                    >
                      <span
                        className={`${styles.taskAgentStatusBox} ${
                          task.isRunning
                            ? styles.taskAgentStatusBoxRunning
                            : styles.taskAgentStatusBoxPending
                        }`}
                        aria-hidden="true"
                      >
                        {task.isRunning ? (
                          <span className={styles.taskAgentStatusDot} />
                        ) : null}
                      </span>
                      <div className={styles.taskAgentItemBody}>
                        <div className={styles.taskAgentItemTitle}>{task.title}</div>
                        <div className={styles.taskAgentItemMeta}>
                          {task.statusLabel}
                          {task.dateLabel ? ` · ${task.dateLabel}` : ""}
                        </div>
                      </div>
                    </button>
                  ))}
                  {remaining > 0 ? (
                    <button
                      type="button"
                      className={styles.taskAgentShowMore}
                      onClick={() => showMore(key)}
                    >
                      Show {Math.min(SHOW_MORE_STEP, remaining)} more
                    </button>
                  ) : null}
                </div>
              );
            })
          ) : (
            <p className={styles.taskAgentEmpty}>
              {searchQuery ? "No active tasks match your search." : "No active tasks yet."}
            </p>
          )}
        </div>
      ) : (
        <div className={styles.taskAgentList}>
          {logGroups.length ? (
            logGroups.map((group) => {
              const key = `log:${group.dateLabel}`;
              const visibleLimit = getVisibleLimit(key);
              const visibleTasks = group.tasks.slice(0, visibleLimit);
              const remaining = Math.max(0, group.tasks.length - visibleLimit);
              return (
                <div key={group.dateLabel} className={styles.taskAgentGroup}>
                  <div className={styles.taskAgentGroupLabel}>{group.dateLabel}</div>
                  {visibleTasks.map((task) => (
                    <button
                      key={task.id}
                      type="button"
                      className={styles.taskAgentItem}
                      onClick={() => onOpenTaskModal(task, "log")}
                    >
                      <span className={styles.taskAgentStatusBoxDone} aria-hidden="true">
                        <svg
                          width="8"
                          height="8"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      </span>
                      <div className={styles.taskAgentItemBody}>
                        <div className={styles.taskAgentItemTitleDone}>{task.title}</div>
                        <div className={styles.taskAgentItemMetaDone}>
                          {task.outcomeLabel} · {task.focusArea}
                        </div>
                      </div>
                    </button>
                  ))}
                  {remaining > 0 ? (
                    <button
                      type="button"
                      className={styles.taskAgentShowMore}
                      onClick={() => showMore(key)}
                    >
                      Show {Math.min(SHOW_MORE_STEP, remaining)} more
                    </button>
                  ) : null}
                </div>
              );
            })
          ) : (
            <p className={styles.taskAgentEmpty}>
              {searchQuery ? "No completed tasks match your search." : "No completed tasks yet."}
            </p>
          )}
        </div>
      )}
    </>
  );
}

export default AppRightRailTaskAgentSection;
